import {
    Button,
    DropDown,
    Form,
    Modal,
    requiredRule,
    useErrorState,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { addFundsTransferSetting, changeFundsTransferSetting } from 'axios/fundsTransferApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { Bank } from 'models/api/bank';
import { UnloadSetting, UnloadSettingCreate } from 'models/api/fundsTransfer';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import 'scss/Main.scss';
import ModalFundsTransferSettingOrder from './ModalFundsTransferSettingOrder';
import ModalFundsTransferSettingPeriod from './ModalFundsTransferSettingPeriod';

type ModalFundsTransferSettingProps = {
    toggle: () => void;
    setting?: UnloadSetting;
    updateList: () => void;
    brokers: Bank[];
    bankGroups: DropDown[];
    clientTypes: DropDown[];
    brokersLoading: boolean;
    dictLoading: boolean;
};

const ModalFundsTransferSetting = ({
    toggle,
    setting: currentSetting,
    updateList,
    brokers,
    bankGroups,
    clientTypes,
    dictLoading,
    brokersLoading,
}: ModalFundsTransferSettingProps) => {
    const { t } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const brokerList: DropDown[] = brokers.map((x: Bank) => ({ id: x.id, name: x.shortName }));

    const defaultSetting: UnloadSettingCreate = {
        brokerBankId: null,
        clientBanksGroupCode: null,
        clientTypeFilter: null,
        isActive: false,
        name: '',
        orders: [],
        periods: [],
        useOrders: false,
    };

    const [setting, setSetting] = useState({ ...defaultSetting, ...currentSetting });

    useEffect(() => {
        if (!currentSetting) {
            setSetting({ ...setting, brokerBankId: brokers.length ? brokers[0].id : null });
        }
    }, [brokers]);

    const settingsMutation = useMutation(
        (setting: UnloadSetting) =>
            currentSetting
                ? changeFundsTransferSetting(api, { ...setting, id: currentSetting.id })
                : addFundsTransferSetting(api, { ...setting }),
        {
            onSuccess: () => {
                toggle();
                updateList();
            },
            onError: (e) => {
                handleRequestError(e);
            },
        }
    );

    const saveChanges = async (setting: UnloadSetting) => {
        settingsMutation.mutate(setting);
    };

    const formHasRole = () =>
        currentSetting
            ? hasRole('fundsTransfersSettingsUpdate')
            : hasRole('fundsTransfersSettingsCreate');

    const bankGroupsRole = () => (currentSetting ? false : hasRole('fundsTransfersSettingsCreate'));

    return (
        <Modal size="lg" isOpen={true} toggle={toggle} scrollable className="brands__modal">
            <Form<UnloadSetting>
                defaultValues={setting}
                clearAfterSubmit={false}
                onSubmit={(data) => {
                    saveChanges(data);
                }}
                className="d-contents"
            >
                <ModalHeader toggle={() => toggle()}>
                    <Trans i18nKey="BackOfficeUnloadSetting" />
                </ModalHeader>

                <ModalBody className="custom-scrollbar">
                    <Row>
                        <Col>
                            <ValidatedInput<UnloadSetting>
                                label={t('BackOfficeName')}
                                disabled={!formHasRole() || settingsMutation.isLoading}
                                name="name"
                                rules={{
                                    required: requiredRule,
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedDropDownList
                                name="clientBanksGroupCode"
                                label={t('BackOfficeClientAccountBank')}
                                disabled={!bankGroupsRole() || settingsMutation.isLoading}
                                isLoading={dictLoading}
                                items={bankGroups}
                                onlyListValue={true}
                                isSearchable={false}
                                isClearable={false}
                                defaultItems={[{ id: null, name: t('BackOfficeOtherBanks') }]}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedDropDownList<UnloadSetting>
                                name="brokerBankId"
                                label={t('BackOfficeFundsInTheBank')}
                                disabled={!formHasRole() || settingsMutation.isLoading}
                                isLoading={brokersLoading}
                                items={brokerList}
                                onlyListValue={true}
                                isSearchable={false}
                                isClearable={false}
                                rules={{
                                    required: requiredRule,
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedInput<UnloadSetting>
                                id="unloadSettingEnabledCheckbox"
                                type="checkbox"
                                label={t('BackOfficeUnloadSettingEnabled')}
                                disabled={!formHasRole() || settingsMutation.isLoading}
                                name="isActive"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedDropDownList
                                name="clientTypeFilter"
                                label={t('BackOfficeClientTypeFilter')}
                                disabled={!formHasRole() || settingsMutation.isLoading}
                                isLoading={dictLoading}
                                items={clientTypes}
                                onlyListValue={true}
                                isSearchable={false}
                                isClearable={false}
                                defaultItems={[{ id: null, name: t('BackOfficeAll') }]}
                            />
                        </Col>
                    </Row>
                    <ModalFundsTransferSettingPeriod
                        disabled={!formHasRole() || settingsMutation.isLoading}
                    />
                    <Row>
                        <Col>
                            <ValidatedInput
                                id="useOrdersCheckbox"
                                type="checkbox"
                                label={t('BackOfficeUnloadSettingUseOrders')}
                                disabled={!formHasRole() || settingsMutation.isLoading}
                                name="useOrders"
                            />
                        </Col>
                    </Row>
                    {
                        <ModalFundsTransferSettingOrder
                            disabled={!formHasRole() || settingsMutation.isLoading}
                        />
                    }
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => toggle()}
                        disabled={settingsMutation.isLoading}
                    >
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button
                        color="success"
                        disabled={
                            dictLoading ||
                            brokersLoading ||
                            !formHasRole() ||
                            settingsMutation.isLoading
                        }
                        isPending={settingsMutation.isLoading}
                        type={'submit'}
                    >
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default ModalFundsTransferSetting;
